import { SEOHead } from "@/components/SEOHead";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";

const TermsOfService = () => {
  const { language } = useLanguage();
  const isRu = language === 'ru';

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead 
        title={isRu ? 'Условия использования' : 'Terms and Conditions'}
        description={isRu 
          ? 'Настоящие Условия использования регулируют ваш доступ к веб-сайту www.aitiminc.com и его использование.'
          : 'These Terms and Conditions govern your access to and use of the website www.aitiminc.com.'}
        canonical="/terms-of-service"
      />
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold text-navy-950 mb-8">
            {isRu ? 'Условия использования AITIM Inc.' : 'Terms and Conditions for AITIM Inc.'}
          </h1>
          
          <div className="prose prose-slate max-w-none text-slate-600 space-y-8">
            <div>
              <p className="font-semibold text-navy-950">
                {isRu ? 'Дата вступления в силу: 01.01.2026' : 'Effective Date: 01/01/2026'}
              </p>
              <p className="mt-4">
                {isRu 
                  ? 'Настоящие Условия использования («Условия») регулируют ваш доступ к веб-сайту www.aitiminc.com («Сайт») и его использование, а также любые услуги, предоставляемые AITIM Inc. («мы», «наш» или «нас»). Заходя на Сайт или используя его и услуги, вы соглашаетесь соблюдать настоящие Условия, а также все применимые законы и нормативные акты. Если вы не согласны с настоящими Условиями, вы не должны заходить на Сайт или использовать услуги.'
                  : 'These Terms and Conditions ("Terms") govern your access to and use of the website www.aitiminc.com ("Site") and any services provided by AITIM Inc. ("we," "our," or "us"). By accessing or using the Site and services, you agree to comply with and be bound by these Terms, as well as any applicable laws and regulations. If you do not agree with these Terms, you must not access or use the Site or services.'}
              </p>
            </div>

            <section>
              <h2 className="text-2xl font-bold text-navy-950 mb-4">
                {isRu ? '1. Использование наших услуг' : '1. Use of Our Services'}
              </h2>
              <p className="mb-4">
                {isRu 
                  ? 'AITIM Inc. предоставляет консультационные услуги, страховые решения, системы управления перевозками (TMS), услуги подписки на безопасность и поддержку («Услуги»). Вы можете получать доступ к Услугам и использовать их в соответствии с настоящими Условиями. Вы соглашаетесь:'
                  : 'AITIM Inc. provides consulting services, insurance solutions, Transportation Management Systems (TMS), safety subscription services, and support ("Services"). You may access and use the Services subject to these Terms. You agree to:'}
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>{isRu ? 'Предоставлять точную, полную и актуальную информацию при использовании наших Услуг.' : 'Provide accurate, complete, and current information when using our Services.'}</li>
                <li>{isRu ? 'Не использовать наши Услуги в любых незаконных целях или в нарушение настоящих Условий.' : 'Not use our Services for any unlawful purpose or in violation of these Terms.'}</li>
                <li>{isRu ? 'Не пытаться вмешиваться в работу нашего Сайта или Услуг или нарушать ее.' : 'Not attempt to interfere with or disrupt the operation of our Site or Services.'}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-navy-950 mb-4">
                {isRu ? '2. Создание учетной записи и безопасность' : '2. Account Creation and Security'}
              </h2>
              <p className="mb-4">
                {isRu 
                  ? 'Для доступа к определенным функциям или Услугам вам может потребоваться создать учетную запись. При создании учетной записи вы соглашаетесь:'
                  : 'To access certain features or Services, you may need to create an account. When creating an account, you agree to:'}
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>{isRu ? 'Предоставлять правдивую, точную и актуальную информацию.' : 'Provide true, accurate, and up-to-date information.'}</li>
                <li>{isRu ? 'Обеспечивать безопасность и конфиденциальность информации вашей учетной записи и пароля.' : 'Keep your account information and password secure and confidential.'}</li>
                <li>{isRu ? 'Немедленно уведомлять нас, если вы подозреваете какое-либо несанкционированное использование вашей учетной записи.' : 'Notify us immediately if you suspect any unauthorized use of your account.'}</li>
              </ul>
              <p className="mt-4">
                {isRu 
                  ? 'Вы несете ответственность за все действия, которые происходят под вашей учетной записью, и мы не несем ответственности за любые убытки, возникшие в результате несанкционированного доступа к вашей учетной записи или ее использования.'
                  : 'You are responsible for all activities that occur under your account, and we are not liable for any losses resulting from unauthorized access or use of your account.'}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-navy-950 mb-4">
                {isRu ? '3. Подписка на услуги' : '3. Service Subscription'}
              </h2>
              <p>
                {isRu 
                  ? 'Подписываясь на наши Услуги, вы соглашаетесь с ценами, условиями выставления счетов и деталями подписки, предоставленными вам. Мы оставляем за собой право изменять наши планы подписки и цены в любое время. О любых изменениях вам будет сообщено заранее. Вы можете отменить подписку в любое время, связавшись с нами.'
                  : 'By subscribing to our Services, you agree to the pricing, billing terms, and subscription details provided to you. We reserve the right to change our subscription plans and pricing at any time. Any changes will be communicated to you in advance. You may cancel your subscription at any time by contacting us.'}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-navy-950 mb-4">
                {isRu ? '4. Оплата и выставление счетов' : '4. Payment and Billing'}
              </h2>
              <p>
                {isRu 
                  ? 'Если ваша подписка или покупка требуют оплаты, вы соглашаетесь оплачивать сборы, связанные с Услугами. Платежи будут обрабатываться через нашу защищенную стороннюю платежную систему. Вы несете ответственность за предоставление точных платежных реквизитов и обеспечение актуальности вашего метода оплаты.'
                  : 'If your subscription or purchase requires payment, you agree to pay the fees associated with the Services. Payments will be processed through our secure third-party payment processor. You are responsible for providing accurate payment details and ensuring that your payment method is up to date.'}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-navy-950 mb-4">
                {isRu ? '5. Коммуникация' : '5. Communication'}
              </h2>
              <p>
                {isRu 
                  ? 'Взаимодействуя с нами через Сайт, вы соглашаетесь на то, чтобы мы связывались с вами через SMS, электронную почту или по телефону в целях, связанных с нашими Услугами, включая управление учетной записью, запросы по счетам и поддержку. Вы можете отказаться от маркетинговых коммуникаций в любое время, но вы продолжите получать важные сообщения, связанные с обслуживанием.'
                  : 'By engaging with us through the Site, you consent to be contacted via SMS, email, or phone for purposes related to our Services, including account management, billing inquiries, and support. You may opt-out of marketing communications at any time, but you will continue to receive essential service-related messages.'}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-navy-950 mb-4">
                {isRu ? '6. Интеллектуальная собственность' : '6. Intellectual Property'}
              </h2>
              <p>
                {isRu 
                  ? 'Контент, дизайн, структура и функциональность Сайта и Услуг, включая, помимо прочего, текст, графику, логотипы, изображения и программное обеспечение, являются собственностью AITIM Inc. и защищены законами об авторском праве, товарных знаках и другими законами об интеллектуальной собственности. Вы не можете копировать, распространять, изменять или создавать производные работы на основе любого контента без нашего явного письменного разрешения.'
                  : 'The content, design, structure, and functionality of the Site and Services, including but not limited to text, graphics, logos, images, and software, are the property of AITIM Inc. and are protected by copyright, trademark, and other intellectual property laws. You may not copy, distribute, modify, or create derivative works from any content without our express written permission.'}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-navy-950 mb-4">
                {isRu ? '7. Поведение пользователя' : '7. User Conduct'}
              </h2>
              <p className="mb-4">
                {isRu ? 'Вы соглашаетесь не:' : 'You agree not to:'}
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>{isRu ? 'Заниматься любой деятельностью, которая нарушает настоящие Условия или является незаконной, клеветнической или вредной для других.' : 'Engage in any activity that violates these Terms or is unlawful, defamatory, or harmful to others.'}</li>
                <li>{isRu ? 'Пытаться получить несанкционированный доступ к любой части Сайта, Услуг или любым системам или сетям, подключенным к ним.' : 'Attempt to gain unauthorized access to any part of the Site, Services, or any systems or networks connected to them.'}</li>
                <li>{isRu ? 'Использовать любые автоматизированные системы (боты, скреперы и т. д.) для доступа к Сайту или Услугам.' : 'Use any automated system (bots, scrapers, etc.) to access the Site or Services.'}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-navy-950 mb-4">
                {isRu ? '8. Условия использования SMS' : '8. SMS Terms & Conditions'}
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-navy-900 mb-2">
                    {isRu ? '8.1- Коммуникация согласия на SMS:' : '8.1- SMS Consent Communication:'}
                  </h3>
                  <p>{isRu ? 'Информация (номера телефонов), полученная в рамках процесса согласия на SMS, не будет передаваться третьим лицам в маркетинговых целях.' : 'The information (Phone Numbers) obtained as part of the SMS consent process will not be shared with third parties for marketing purposes.'}</p>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-navy-900 mb-2">
                    {isRu ? '8.2- Типы SMS-коммуникаций:' : '8.2- Types of SMS Communications:'}
                  </h3>
                  <p className="mb-2">{isRu ? 'Если вы дали согласие на получение текстовых сообщений от AITIM, вы можете получать сообщения, связанные с:' : 'If you have consented to receive text messages from AITIM, you may receive messages related to the following:'}</p>
                  <ul className="list-disc pl-6 mb-2">
                    <li>{isRu ? 'Запрос на обслуживание' : 'Service Request'}</li>
                    <li>{isRu ? 'Последующие сообщения' : 'Follow-up messages'}</li>
                    <li>{isRu ? 'Запросы по счетам' : 'Billing inquiries'}</li>
                  </ul>
                  <p className="italic">{isRu ? 'Пример: «Здравствуйте, это дружеское напоминание о вашем предстоящем тесте на наркотики DOT 12.12.2025. Ответьте STOP, чтобы отказаться от SMS-сообщений в любое время».' : 'Example: "Hello, this is a friendly reminder of your upcoming DOT Drug Screen on 12.12.2025. Reply STOP to opt out of SMS messaging at any time."'}</p>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-navy-900 mb-2">
                    {isRu ? '8.3- Частота сообщений:' : '8.3- Message Frequency:'}
                  </h3>
                  <p>{isRu ? 'Частота сообщений может варьироваться в зависимости от типа коммуникации. Например, вы можете получать до 10 SMS-сообщений в неделю, связанных с вашим [запросом на обслуживание/счетом и т. д.].' : 'Message frequency may vary depending on the type of communication. For example, you may receive up to 10 SMS messages per week related to your [service request/billing, etc.].'}</p>
                  <p className="italic mt-2">{isRu ? 'Пример: «Частота сообщений может варьироваться. Вы можете получать до 10 SMS-сообщений в месяц относительно ваших запросов на обслуживание или статуса учетной записи».' : 'Example: "Message frequency may vary. You may receive up to 10 SMS messages per month regarding your service requests or account status."'}</p>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-navy-900 mb-2">
                    {isRu ? '8.4- Потенциальные сборы за SMS-сообщения:' : '8.4- Potential Fees for SMS Messaging:'}
                  </h3>
                  <p>{isRu ? 'Обратите внимание, что могут применяться стандартные тарифы на сообщения и передачу данных, в зависимости от тарифного плана вашего оператора. Эти сборы могут варьироваться, если сообщение отправляется внутри страны или за границу.' : 'Please note that standard message and data rates may apply, depending on your carrier’s pricing plan. These fees may vary if the message is sent domestically or internationally.'}</p>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-navy-900 mb-2">
                    {isRu ? '8.5- Метод подписки (Opt-In):' : '8.5- Opt-In Method:'}
                  </h3>
                  <p className="mb-2">{isRu ? 'Вы можете подписаться на получение SMS-сообщений от AITIM следующими способами:' : 'You may opt-in to receive SMS messages from AITIM in the following ways:'}</p>
                  <ul className="list-disc pl-6">
                    <li>{isRu ? 'Устно, во время разговора' : 'Verbally, during a conversation'}</li>
                    <li>{isRu ? 'Путем отправки онлайн-формы' : 'By submitting an online form'}</li>
                    <li>{isRu ? 'Заполнив бумажную форму' : 'By filling out a paper form'}</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-navy-900 mb-2">
                    {isRu ? '8.6- Метод отказа (Opt-Out):' : '8.6- Opt-Out Method:'}
                  </h3>
                  <p>{isRu ? 'Вы можете отказаться от получения SMS-сообщений в любое время. Для этого просто ответьте «STOP» на любое полученное вами SMS-сообщение. Кроме того, вы можете связаться с нами напрямую, чтобы запросить удаление из нашего списка рассылки.' : 'You can opt out of receiving SMS messages at any time. To do so, simply reply "STOP" to any SMS message you receive. Alternatively, you can contact us directly to request removal from our messaging list.'}</p>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-navy-900 mb-2">
                    {isRu ? '8.7- Помощь:' : '8.7- Help:'}
                  </h3>
                  <p>{isRu ? 'Если у вас возникли проблемы, вы можете ответить ключевым словом HELP. Или вы можете получить помощь напрямую от нас по нашей ссылке поддержки.' : 'If you are experiencing any issues, you can reply with the keyword HELP. Or, you can get help directly from us at our support link.'}</p>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-navy-900 mb-2">
                    {isRu ? '8.8- Стандартные раскрытия информации о сообщениях:' : '8.8- Standard Messaging Disclosures:'}
                  </h3>
                  <ul className="list-disc pl-6">
                    <li>{isRu ? 'Могут применяться тарифы на сообщения и передачу данных.' : 'Message and data rates may apply.'}</li>
                    <li>{isRu ? 'Вы можете отказаться в любое время, отправив «STOP».' : 'You can opt-out at any time by texting "STOP."'}</li>
                    <li>{isRu ? 'Для получения помощи отправьте «HELP» или посетите наши страницы [Политика конфиденциальности] и [Условия использования].' : 'For assistance, text "HELP" or visit our [Privacy Policy] and [Terms and Conditions] pages.'}</li>
                    <li>{isRu ? 'Частота сообщений может варьироваться' : 'Message frequency may vary'}</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-navy-950 mb-4">
                {isRu ? '9. Конфиденциальность' : '9. Privacy'}
              </h2>
              <p>
                {isRu 
                  ? 'Ваше использование нашего Сайта и Услуг также регулируется нашей Политикой конфиденциальности, которая описывает, как мы собираем, используем и защищаем вашу личную информацию.'
                  : 'Your use of our Site and Services is also governed by our Privacy Policy, which outlines how we collect, use, and protect your personal information.'}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-navy-950 mb-4">
                {isRu ? '10. Ограничение ответственности' : '10. Limitations of Liability'}
              </h2>
              <p className="mb-4">
                {isRu 
                  ? 'В максимальной степени, разрешенной законом, AITIM Inc. не несет ответственности за любые прямые, косвенные, случайные, специальные или последующие убытки, возникшие в результате:'
                  : 'To the fullest extent permitted by law, AITIM Inc. is not liable for any direct, indirect, incidental, special, or consequential damages resulting from:'}
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>{isRu ? 'Вашего использования или невозможности использования Сайта или Услуг.' : 'Your use or inability to use the Site or Services.'}</li>
                <li>{isRu ? 'Любых ошибок или упущений в контенте Сайта.' : 'Any errors or omissions in the content of the Site.'}</li>
                <li>{isRu ? 'Любых перерывов или задержек в Услугах.' : 'Any interruptions or delays in the Services.'}</li>
              </ul>
              <p className="mt-4">
                {isRu 
                  ? 'Наша общая ответственность в любом случае ограничивается суммой, которую вы заплатили за конкретную услугу, вызвавшую претензию.'
                  : 'Our total liability, in any case, is limited to the amount you have paid for the specific service that caused the claim.'}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-navy-950 mb-4">
                {isRu ? '11. Возмещение ущерба' : '11. Indemnification'}
              </h2>
              <p>
                {isRu 
                  ? 'Вы соглашаетесь возмещать ущерб, защищать и ограждать AITIM Inc., ее должностных лиц, сотрудников, подрядчиков и агентов от любых претензий, обязательств, убытков, потерь и расходов (включая судебные издержки), возникающих в связи с:'
                  : 'You agree to indemnify, defend, and hold harmless AITIM Inc. and its officers, employees, contractors, and agents from any claims, liabilities, damages, losses, and expenses (including legal fees) arising out of:'}
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>{isRu ? 'Вашим использованием Сайта или Услуг.' : 'Your use of the Site or Services.'}</li>
                <li>{isRu ? 'Вашим нарушением настоящих Условий.' : 'Your violation of these Terms.'}</li>
                <li>{isRu ? 'Вашим нарушением прав интеллектуальной собственности других лиц.' : 'Your infringement of the intellectual property rights of others.'}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-navy-950 mb-4">
                {isRu ? '12. Прекращение действия' : '12. Termination'}
              </h2>
              <p>
                {isRu 
                  ? 'Мы можем приостановить или прекратить ваш доступ к Сайту или Услугам в любое время, без уведомления, по любой причине, в том числе если вы нарушите настоящие Условия. После прекращения ваше право на использование Услуг прекратится, но вы останетесь ответственными за любые неоплаченные платежи.'
                  : 'We may suspend or terminate your access to the Site or Services at any time, without notice, for any reason, including if you breach these Terms. Upon termination, your right to use the Services will cease, but you will remain responsible for any outstanding payments.'}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-navy-950 mb-4">
                {isRu ? '13. Регулирующее право' : '13. Governing Law'}
              </h2>
              <p>
                {isRu 
                  ? 'Настоящие Условия регулируются и толкуются в соответствии с законами штата Иллинойс, США. Любые юридические действия или разбирательства в соответствии с настоящими Условиями должны возбуждаться в судах штата Иллинойс, и вы соглашаетесь на юрисдикцию этих судов.'
                  : 'These Terms are governed by and construed in accordance with the laws of Illinois, USA. Any legal actions or proceedings under these Terms shall be brought in the courts of Illinois, and you consent to the jurisdiction of those courts.'}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-navy-950 mb-4">
                {isRu ? '14. Изменения в Условиях' : '14. Modifications to Terms'}
              </h2>
              <p>
                {isRu 
                  ? 'AITIM Inc. оставляет за собой право изменять настоящие Условия в любое время. Любые изменения будут опубликованы на этой странице, и «Дата вступления в силу» будет соответствующим образом обновлена. Продолжая использовать Сайт или Услуги после внесения любых изменений, вы соглашаетесь с обновленными Условиями.'
                  : 'AITIM Inc. reserves the right to modify these Terms at any time. Any changes will be posted on this page, and the "Effective Date" will be updated accordingly. By continuing to use the Site or Services after any modifications, you agree to the updated Terms.'}
              </p>
            </section>

            <section className="pt-8 border-t border-slate-200">
              <h2 className="text-2xl font-bold text-navy-950 mb-4">
                {isRu ? 'Контактная информация' : 'Contact Information'}
              </h2>
              <p className="mb-4">
                {isRu 
                  ? 'Если у вас есть вопросы по поводу настоящих Условий, пожалуйста, свяжитесь с нами по адресу:'
                  : 'If you have any questions about these Terms, please contact us at:'}
              </p>
              <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
                <p className="font-bold text-navy-950 mb-1">AITIM Inc.</p>
                <p>1114 N Arlington Heights Rd Unit 201</p>
                <p>Arlington Heights IL 60004</p>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsOfService;
